/**
 * The hero video on the home page. Replaces jquery.background-video.js, whose
 * only remaining job was to fade the video in and stop it after two minutes --
 * and which was the last consumer of jQuery outside the tab strip.
 *
 * Same settings the plugin was called with in htmlFooter: fade over 750 ms (the
 * stylesheet owns the duration), pause after 120 s, fade out when it does.
 *
 * `prefers-reduced-motion` was not honoured before. A looping background video
 * is exactly what that setting is about, so under `reduce` the video is not
 * loaded at all and the hero picture of the same scene stays in view. The
 * download is stopped by `media` on the two <source> elements in
 * 11_backgroundVideo.chunk, NOT here: a module at the end of <body> runs long
 * after the browser has started fetching. Measured 2026-09-20 before the fix:
 * 1,680 KB, every visit, for a video that was then paused and never seen.
 *
 * The pause button stays hidden in that case. It asks WCAG 2.2.2 for a control
 * over content that moves by itself; with nothing loaded, nothing moves.
 */

const SELECTOR = '[data-background-video]'
const PAUSE_AFTER = 120_000

function setup (video) {
  const show = () => video.classList.add('is-visible')
  const hide = () => video.classList.remove('is-visible')

  /* FIRST, and it used to be last. Both <source> elements now carry
     `media="(prefers-reduced-motion: no-preference)"`, so under `reduce` the
     resource selection algorithm picks nothing, no bytes are fetched and the
     empty element shows the hero picture beneath it.

     That makes the pause button a control with nothing behind it, so it is
     left `hidden` rather than revealed. WCAG 2.2.2 is not engaged: it asks for
     a control over content that moves by itself, and nothing here moves.

     `autoplay` still comes off and the video is still paused, for the engine
     that ignores `media` on a <source>. None of the three do -- measured at
     0 KB in all of them -- but the guard costs two lines and the failure it
     covers is a video playing behind text for someone who asked for no motion.

     Returning here is what keeps the button hidden; everything below assumes a
     resource exists. */
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    video.removeAttribute('autoplay')
    video.pause()
    show()
    return
  }

  /* WCAG 2.2.2, Level A. The video starts on its own and runs for two minutes,
     so there has to be a control -- a11y audit finding 3.3. The button ships
     `hidden` and is revealed here, because a pause button on a page where this
     script did not run has nothing to act on. */
  const toggle = document.querySelector('[data-video-toggle]')
  let timer = null

  /* Declared out here rather than inside the `if (toggle)` below, because
     start() calls it when a play attempt is refused and there may be no
     toggle at all on a page that carries the video without the control. */
  const reflect = () => {
    if (!toggle) return
    const paused = video.paused
    toggle.classList.toggle('is-paused', paused)
    toggle.setAttribute('aria-label', paused
      ? toggle.dataset.labelPlay
      : toggle.dataset.labelPause)
  }

  if (toggle) {
    toggle.hidden = false
    toggle.addEventListener('click', () => {
      /* The visitor outranks the timer. Once they have touched the control,
         the two-minute stop must not come along later and undo the choice --
         pressing play and watching it fade out ninety seconds later would read
         as the button not having worked. */
      if (timer !== null) {
        clearTimeout(timer)
        timer = null
      }
      if (video.paused) {
        show()
        video.play()
      } else {
        video.pause()
      }
    })
    /* Driven by the element, not by the click, so the label is still right
       when something else pauses it -- the timer below, or the browser when
       the tab goes to the background. */
    video.addEventListener('play', reflect)
    video.addEventListener('pause', reflect)
    reflect()
  }

  // iOS Safari does not autoplay a video at opacity 0 and then buffers
  // nothing, so waiting for `canplay` waited forever. play() is allowed
  // without a gesture because the video is muted. If it is refused while
  // invisible, the retry runs visible. Low Power Mode refuses both: the video
  // hides again so the hero picture shows instead of a frozen first frame, and
  // the control offers "play". The fade starts once playback has started,
  // which may already be the case when this runs.
  video.play().then(show, () => {
    show()
    video.play().catch(() => {
      hide()
      reflect()
    })
  })

  timer = setTimeout(() => {
    timer = null
    hide()
    /* After the fade, not with it -- a pause mid-fade freezes a half-visible
       frame that then sits there. The duration matches the stylesheet. */
    setTimeout(() => video.pause(), 750)
  }, PAUSE_AFTER)
}

export function initBackgroundVideo (root = document) {
  root.querySelectorAll(SELECTOR).forEach(setup)
}
